import { ClipboardIcon } from "@radix-ui/react-icons";
import {
  OrganizationResponseForAdmins,
  OrganizationsService,
} from "client/openapi";
import type { PersonRow } from "client/openapi/models/PersonRow";
import CreateTaskDialog from "components/Dashboard/ManageTasksDialog/create";
import { announceTasksChanged } from "components/Dashboard/ManageTasksDialog/tasksChanged";
import { Dialog, DialogTrigger } from "components/Dialog";
import { useContext, useEffect, useState } from "react";
import Notifications from "util/notifications";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import IconTip from "./IconTip";
import { personName } from "./person";

/**
 * The row's add-task icon: the New task dialog, about this person and
 * assigned to the signed-in admin, so it is an admin task. The person and
 * their family are suggested first in its pickers. A save tells the
 * page (the tadpole:tasks-changed event) so the tasks count refreshes.
 */
export default function QuickAddTask({
  row,
  orgId,
}: {
  row: PersonRow;
  orgId: number;
}) {
  const { account } = useContext(OrgRolesAndAccountContext);
  const [open, setOpen] = useState(false);
  // The organization's people, for the dialog's other selects.
  const [org, setOrg] = useState<OrganizationResponseForAdmins>();

  useEffect(() => {
    if (!open || org) return;
    OrganizationsService.getOrganization({ orgId })
      .then((result) => setOrg(result as OrganizationResponseForAdmins))
      .catch(() => Notifications.error("Could not load the organization."));
  }, [open, org, orgId]);

  const name = personName(row) || "No name";
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <IconTip label="Add a task">
        <DialogTrigger asChild>
          <button
            type="button"
            className="people-panel__icon"
            aria-label="Add a task"
          >
            <ClipboardIcon />
          </button>
        </DialogTrigger>
      </IconTip>
      {open && (
        <CreateTaskDialog
          students={org?.students}
          parents={org?.parents}
          tutors={org?.tutors}
          admins={org?.admins}
          initialTask={{
            tagged: [{ role: row.kind, id: row.id, label: name }],
            assigned: account
              ? [
                  {
                    role: "admin",
                    id: account.reference_id,
                    label: `${account.first_name} ${account.last_name}`,
                  },
                ]
              : [],
          }}
          suggested={[row, ...row.family].map((person) => ({
            role: person.kind,
            id: person.id,
            label: personName(person),
          }))}
          onClose={() => setOpen(false)}
          refreshTasks={announceTasksChanged}
        />
      )}
    </Dialog>
  );
}
